import { Cross2Icon } from '@radix-ui/react-icons';
import { Box, Button, Card, Flex, Heading, IconButton } from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonTabs } from 'components/common/tabs';
import { VideoLibrarySidebarDetailsTab } from 'components/sections/video-library/sidebar/details-tab';
import { VideoLibrarySidebarKnFTab } from 'components/sections/video-library/sidebar/keying-framing-tab';
import { VideoLibrarySidebarScalingTab } from 'components/sections/video-library/sidebar/scaling-tab';
import { VideoLibrarySetDefaultButton } from 'components/sections/video-library/sidebar/set-default-button';
import useVideoLibrarySidebarStore from 'components/sections/video-library/sidebar/use-video-library-store';
import { t } from 'i18next';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useEffect, useState } from 'react';
import { useVideosStore } from 'stores/use-videos-store';

enum TabKey {
  Details = 'Details',
  KeyingFraming = 'KeyingFraming',
  Scaling = 'Scaling',
}

export const VideoLibrarySidebar = () => {
  const model = useVideoLibrarySidebarStore((s) => s.model);
  const selectedVideo = useVideosStore((s) => s.selectedVideo);

  const [activeTab, setActiveTab] = useState(TabKey.Details);

  // reset to details tab when changing videos
  useEffect(() => {
    if (!selectedVideo) {
      return;
    }

    useVideoLibrarySidebarStore.getState().initModel(selectedVideo);
    setActiveTab(TabKey.Details);
  }, [selectedVideo?._id]);

  if (!selectedVideo) {
    // shouldn't trigger
    return null;
  }

  return (
    <ErrorBoundary identifier="VideoLibrarySidebar">
      <Card
        style={{
          padding: 'var(--space-5)',
          height: '100%',
        }}
      >
        <Flex direction="column" gap="2" style={{ height: '100%' }}>
          {/* header */}
          <Flex
            justify="between"
            align="center"
            gap="2"
            style={{ width: '100%' }}
          >
            <Heading size="5" truncate>
              {t('videos.edit-video')}
            </Heading>

            <IconButton
              color={RADIX.COLOR.NEUTRAL}
              variant="soft"
              className="btn-floating"
              onClick={() =>
                useVideosStore.setState({ selectedVideo: undefined })
              }
            >
              <Cross2Icon />
            </IconButton>
          </Flex>

          {/* tabs */}
          <Box flexGrow="1" style={{ overflow: 'scroll' }}>
            <CommonTabs
              value={activeTab}
              onValueChange={(value) => setActiveTab(value as TabKey)}
              gap="1"
              tabs={[
                {
                  value: TabKey.Details,
                  label: 'common.details',
                  content: <VideoLibrarySidebarDetailsTab />,
                },
                {
                  value: TabKey.KeyingFraming,
                  label: 'videos.keying-and-framing',
                  content: <VideoLibrarySidebarKnFTab />,
                },
                {
                  value: TabKey.Scaling,
                  label: 'common.scaling',
                  content: (
                    <VideoLibrarySidebarScalingTab identifier="VideoLibrarySidebarScalingTab" />
                  ),
                },
              ]}
            />
          </Box>

          {/* footer */}
          <Flex justify="end" gap="3">
            <VideoLibrarySetDefaultButton video={selectedVideo} />

            <Button
              onClick={() => {
                if (!model) {
                  return;
                }

                useVideosStore.getState().updateVideos([
                  {
                    ...model,
                    _id: selectedVideo._id,
                  },
                ]);
              }}
            >
              {t('common.save')}
            </Button>
          </Flex>
        </Flex>
      </Card>
    </ErrorBoundary>
  );
};
